import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useClubAdmin } from '@/hooks/useClubAdmin';
import { supabase } from '@/integrations/supabase/client';

// Access levels for each module
export type AccessLevel = 'none' | 'read' | 'write' | 'full';

// Default permissions per role when no custom permissions are set
const DEFAULT_ROLE_PERMISSIONS: Record<string, Record<string, AccessLevel>> = {
  owner: {
    dashboard: 'full', members: 'full', staff: 'full', courses: 'full',
    teesheet: 'full', services: 'full', revenue: 'full', events: 'full', coaching: 'full',
    channels: 'full', announcements: 'full', feedback: 'full', analytics: 'full', settings: 'full',
  },
  admin: {
    dashboard: 'full', members: 'full', staff: 'full', courses: 'full',
    teesheet: 'full', services: 'full', revenue: 'full', events: 'full', coaching: 'full',
    channels: 'full', announcements: 'full', feedback: 'full', analytics: 'full', settings: 'write',
  },
  coach: {
    dashboard: 'read', members: 'read', staff: 'none', courses: 'read',
    teesheet: 'none', services: 'none', revenue: 'none', events: 'read', coaching: 'full',
    channels: 'write', announcements: 'read', feedback: 'none', analytics: 'read', settings: 'none',
  },
};

// Map permission module keys to route paths
export const PERMISSION_TO_ROUTES: Record<string, string[]> = {
  dashboard: ['/club-admin/dashboard'],
  members: ['/club-admin/members', '/club-admin/member-progress'],
  staff: ['/club-admin/staff'],
  courses: ['/club-admin/courses'],
  teesheet: ['/club-admin/tee-sheet', '/club-admin/tee-check-in'],
  services: ['/club-admin/services'],
  revenue: ['/club-admin/revenue', '/club-admin/transactions'],
  events: ['/club-admin/events'],
  coaching: ['/club-admin/coaching'],
  communication: ['/club-admin/communication'],
  announcements: ['/club-admin/announcements'],
  feedback: ['/club-admin/feedback'],
  analytics: ['/club-admin/analytics'],
  settings: ['/club-admin/settings'],
};

export interface UseStaffPermissionsResult {
  permissions: Record<string, AccessLevel> | null;
  isLoading: boolean;
  error: Error | null;
  hasAccess: (moduleKey: string, minLevel?: AccessLevel) => boolean;
  canAccessRoute: (route: string) => boolean;
  getAllowedRoutes: () => string[];
}

/**
 * Hook to fetch and check staff permissions for the current user
 * Permissions are stored in user_club_memberships.staff_notes as JSON
 */
export function useStaffPermissions(): UseStaffPermissionsResult {
  const { user } = useAuth();
  const { currentClub } = useClubAdmin();
  const userId = user?.id;
  const clubId = currentClub?.id;
  const userRole = currentClub?.admin_role as string | undefined;

  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['staff-permissions', userId, clubId],
    queryFn: async () => {
      if (!userId || !clubId) return null;

      // Owners and admins get full access by default
      if (userRole === 'owner') {
        return DEFAULT_ROLE_PERMISSIONS.owner;
      }

      // Fetch custom permissions from staff_notes
      const { data, error } = await supabase
        .from('user_club_memberships')
        .select('staff_notes, role')
        .eq('user_id', userId)
        .eq('club_id', clubId)
        .single();

      if (error) {
        console.error('Error fetching staff permissions:', error);
        return null;
      }

      // Try to parse accessLevels from staff_notes JSON
      if (data?.staff_notes) {
        try {
          const notesData = JSON.parse(data.staff_notes);
          if (notesData.accessLevels) {
            return notesData.accessLevels as Record<string, AccessLevel>;
          }
        } catch {
          // staff_notes is not JSON or doesn't have accessLevels
        }
      }

      // Fall back to default permissions for the user's role
      const role = data?.role || userRole || 'member';
      return DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS.coach;
    },
    enabled: !!userId && !!clubId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  /**
   * Check if user has access to a specific module
   * @param moduleKey - The permission module key (e.g., 'events', 'courses')
   * @param minLevel - Minimum access level required ('read', 'write', 'full'). Default: 'read'
   */
  const hasAccess = (moduleKey: string, minLevel: AccessLevel = 'read'): boolean => {
    // Owners always have full access
    if (userRole === 'owner') return true;

    if (!permissions) return false;

    const userLevel = permissions[moduleKey];
    if (!userLevel || userLevel === 'none') return false;

    // Access level hierarchy: none < read < write < full
    const levelOrder: Record<AccessLevel, number> = {
      none: 0,
      read: 1,
      write: 2,
      full: 3,
    };

    return levelOrder[userLevel] >= levelOrder[minLevel];
  };

  /**
   * Check if user can access a specific route
   * @param route - The route path (e.g., '/club-admin/events')
   */
  const canAccessRoute = (route: string): boolean => {
    // Owners always have full access
    if (userRole === 'owner') return true;

    // Admins have full access
    if (userRole === 'admin') return true;

    // Find which permission module this route belongs to
    for (const [moduleKey, routes] of Object.entries(PERMISSION_TO_ROUTES)) {
      const matchesRoute = routes.some(r =>
        route === r ||
        route.startsWith(r + '/') ||
        r.startsWith(route)
      );

      if (matchesRoute) {
        return hasAccess(moduleKey, 'read');
      }
    }

    // Default: allow access to unknown routes (safer for routing)
    return true;
  };

  /**
   * Get all routes the user is allowed to access
   */
  const getAllowedRoutes = (): string[] => {
    // Owners and admins get all routes
    if (userRole === 'owner' || userRole === 'admin') {
      return Object.values(PERMISSION_TO_ROUTES).flat();
    }

    if (!permissions) return [];

    const allowedRoutes: string[] = [];
    for (const [moduleKey, routes] of Object.entries(PERMISSION_TO_ROUTES)) {
      if (hasAccess(moduleKey, 'read')) {
        allowedRoutes.push(...routes);
      }
    }

    return allowedRoutes;
  };

  return {
    permissions,
    isLoading,
    error: error as Error | null,
    hasAccess,
    canAccessRoute,
    getAllowedRoutes,
  };
}
